import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  EmployerApiKey,
  RevocationCadence,
  RosterSyncSettings,
} from './apiRosterSyncTypes'
import {
  generateApiKey,
  getRosterSync,
  revokeApiKey,
  updateRosterSyncSettings,
} from './apiRosterSyncActions'

export interface ApiRosterSyncState {
  settings: RosterSyncSettings
  keys: EmployerApiKey[]
  rawKey: string | null
  rawKeyExpiresAt: string | null
  loaded: boolean
  saving: boolean
}

export const initialState: ApiRosterSyncState = {
  settings: {
    enabled: false,
    revocationCadence: 'never',
  },
  keys: [],
  rawKey: null,
  rawKeyExpiresAt: null,
  loaded: false,
  saving: false,
}

const apiRosterSyncSlice = createSlice({
  name: 'apiRosterSync',
  initialState,
  reducers: {
    getRosterSyncSuccess(
      state,
      action: PayloadAction<{
        settings: RosterSyncSettings
        keys: EmployerApiKey[]
      }>
    ) {
      state.settings = action.payload.settings
      state.keys = action.payload.keys
      state.loaded = true
    },
    getRosterSyncFailed(state) {
      state.loaded = true
    },
    setSaving(state, action: PayloadAction<boolean>) {
      state.saving = action.payload
    },
    updateSettingsSuccess(state, action: PayloadAction<RosterSyncSettings>) {
      state.settings = action.payload
      state.saving = false
    },
    updateSettingsFailed(state, _action: PayloadAction<string | undefined>) {
      state.saving = false
    },
    generateKeySuccess(state, action: PayloadAction<EmployerApiKey>) {
      const { rawKey, ...rest } = action.payload
      state.keys = [rest, ...state.keys.filter((k) => k.id !== rest.id)]
      state.rawKey = rawKey ?? null
      state.rawKeyExpiresAt = rest.expiresAt
      state.saving = false
    },
    generateKeyFailed(state, _action: PayloadAction<string | undefined>) {
      state.saving = false
    },
    revokeKeySuccess(state, action: PayloadAction<EmployerApiKey>) {
      state.keys = state.keys.map((k) =>
        k.id === action.payload.id ? action.payload : k
      )
      state.saving = false
    },
    revokeKeyFailed(state, _action: PayloadAction<string | undefined>) {
      state.saving = false
    },
    clearRawKey(state) {
      state.rawKey = null
      state.rawKeyExpiresAt = null
    },
  },
})

export const apiRosterSyncReducer = apiRosterSyncSlice.reducer
export const apiRosterSyncActions = {
  ...apiRosterSyncSlice.actions,
  getRosterSync,
  updateRosterSyncSettings,
  generateApiKey,
  revokeApiKey,
  dismissRawKey: apiRosterSyncSlice.actions.clearRawKey,
}

export type { RevocationCadence }
